import { type ReactElement, createContext, useContext, useState } from 'react';
import { DEFAULT_PLAYER_NAME } from '../../constants/player';
import { getAllPlayerNames } from '../../sources/player';
import { getAllThemeNames } from '../../sources/theme';

interface RunContext {
    player: string;
    theme: string;
    players: string[];
    themes: string[];
    setTheme: (theme: string) => void;
    setPlayer: (player: string) => void;
    getThemes: (environmentId?: string) => Promise<void>;
    getPlayers: () => Promise<void>;
}

interface Props {
    children?: ReactElement;
}

const Context = createContext<RunContext | undefined>(undefined);

const RunProvider = (props: Props) => {
    const [player, setPlayer] = useState<string>(DEFAULT_PLAYER_NAME);
    const [theme, setTheme] = useState<string>(DEFAULT_PLAYER_NAME);
    const [players, setPlayers] = useState<string[]>([]);
    const [themes, setThemes] = useState<string[]>([]);

    const getPlayers = async () => {
        const fetchedPlayers = await getAllPlayerNames();
        setPlayers(fetchedPlayers);

        if (Array.isArray(fetchedPlayers) && fetchedPlayers.indexOf(player) === -1) {
            setPlayer(DEFAULT_PLAYER_NAME);
        }
    };

    const getThemes = async (environmentId?: string) => {
        const fetchedThemes = await getAllThemeNames(environmentId);
        setThemes(fetchedThemes);

        if (Array.isArray(fetchedThemes) && fetchedThemes.indexOf(theme) === -1) {
            setTheme(DEFAULT_PLAYER_NAME);
        }
    };

    const contextValue: RunContext = {
        player,
        theme,
        setPlayer,
        setTheme,
        players,
        themes,
        getPlayers,
        getThemes,
    };

    return <Context.Provider value={contextValue}>{props.children}</Context.Provider>;
};

const useRun = () => {
    const context = useContext(Context);
    if (context === undefined) {
        throw new Error('useRun must be used within a RunProvider');
    }
    return context;
};

export { RunProvider, useRun };
